<template>
  <base-drawer
    :show="show"
    :title="isEdit ? 'Edit affiliate' : 'Create affiliate'"
    width="480px"
    @close="onClose"
  >
    <drawer-section title="Details" icon="icon-single-02">
      <base-input label="Name" placeholder="Person or business name" v-model="form.name" :error="errs.name" />
      <base-input label="Email" placeholder="affiliate@example.com" v-model="form.email" :error="errs.email" />
      <info-hint v-if="isEdit">
        Changing the email relinks this affiliate to that identity. History is unaffected.
      </info-hint>
      <label>Type</label>
      <div class="mb-3">
        <segmented-toggle v-model="form.type" size="sm" :options="typeOptions" />
      </div>
      <base-input label="Tier" :error="errs.affiliate_tier_id">
        <el-select class="select-primary" v-model="form.affiliate_tier_id" placeholder="Select a tier">
          <el-option class="select-primary" v-for="t in tierOptions" :key="t.id" :value="t.id" :label="t.name" />
        </el-select>
      </base-input>
    </drawer-section>

    <drawer-section title="Attribution" icon="icon-link-72">
      <template v-if="!isEdit">
        <label>Source</label>
        <base-input class="mb-0" :error="errs.ref_code">
          <el-input
            v-model="form.ref_code"
            placeholder="Leave blank to auto generate"
          >
            <template #append>
              <button
                class="generate-ref-append"
                type="button"
                @click="generateRefCode"
              >Generate</button>
            </template>
          </el-input>
        </base-input>
        <info-hint>
          The affiliate's ref code, used as utm_source on the booking link. Leave blank to auto generate. Cannot be changed later.
        </info-hint>
        <base-input label="Medium" placeholder="affiliate" v-model="form.utm_medium" :error="errs.utm_medium" />
        <base-input label="Campaign" placeholder="affiliate" v-model="form.utm_campaign" :error="errs.utm_campaign" />
        <info-hint icon="icon-link-72">
          These become utm_medium and utm_campaign on the booking link and group this affiliate's bookings in reports. Lowercase letters, numbers, dashes, and underscores only. Cannot be changed later.
        </info-hint>
        <label>Booking link preview</label>
        <base-input class="mb-3" :value="linkPreview" disabled />
      </template>
      <template v-else>
        <base-input label="Source" :value="affiliate ? affiliate.ref_code : ''" disabled />
        <base-input label="Medium" :value="affiliate ? affiliate.utm_medium : ''" disabled />
        <base-input label="Campaign" :value="affiliate ? affiliate.utm_campaign : ''" disabled />
      </template>
      <base-input label="Linked promo codes (optional)" :error="errs.promo_code_ids">
        <el-select class="select-primary" multiple clearable filterable
          v-model="form.promo_code_ids" placeholder="Select promo codes">
          <el-option class="select-primary" v-for="p in promos" :key="p.id" :value="p.id" :label="p.name" />
        </el-select>
      </base-input>
      <info-hint>
        Bookings using a linked promo code earn commission for this affiliate
        even without the tracking link. A promo code can belong to one affiliate only.
      </info-hint>
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button type="primary" size="sm" :disabled="saving" @click="save">
          <i v-if="saving" class="el-icon-loading mr-1"></i>{{ isEdit ? 'Save' : 'Create affiliate' }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION, BOOKING_URL } from '@/api-config'
import { buildAffiliateLink, isUtmValueValid, normalizeUtmValue, DEFAULT_UTM_VALUE } from '@/util/affiliateLink'
import { scrollToFirstError } from '@/util/scrollToFirstError'

const props = defineProps({
  show: { type: Boolean, default: false },
  affiliate: { type: Object, default: null },
  tiers: { type: Array, default: () => [] },
  promos: { type: Array, default: () => [] }
})

const emit = defineEmits(['close', 'saved'])

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const groupCode = JSON.parse(localStorage.getItem('group') || '{}').code
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const typeOptions = [
  { value: 'influencer', label: 'Influencer' },
  { value: 'local_business', label: 'Local business' },
  { value: 'player', label: 'Player' }
]

const isEdit = computed(() => !!(props.affiliate && props.affiliate.id))

// Archived tiers are hidden from the select, except the tier the affiliate
// being edited already has.
const tierOptions = computed(() =>
  (props.tiers as any[]).filter((t: any) =>
    t.is_archived === 0 || (props.affiliate && t.id === props.affiliate.affiliate_tier_id)))

const saving = ref(false)
const errs = ref<any>({})
const form = ref<any>(defaultForm())

// Live preview of the booking link. Blank ref code shows a placeholder so
// the operator sees where their value lands.
const linkPreview = computed(() => buildAffiliateLink(BOOKING_URL, groupCode, {
  ref_code: form.value.ref_code && form.value.ref_code.trim() ? form.value.ref_code : 'your-ref-code',
  utm_medium: form.value.utm_medium,
  utm_campaign: form.value.utm_campaign
}))

function defaultForm() {
  return {
    name: '', email: '', type: 'influencer', affiliate_tier_id: null, promo_code_ids: [], ref_code: '',
    utm_medium: DEFAULT_UTM_VALUE, utm_campaign: DEFAULT_UTM_VALUE
  }
}

function loadForm() {
  errs.value = {}
  if (props.affiliate) {
    form.value = {
      name: props.affiliate.name,
      email: props.affiliate.email,
      type: props.affiliate.type,
      affiliate_tier_id: props.affiliate.affiliate_tier_id,
      promo_code_ids: (props.affiliate.promo_codes || []).map((p: any) => p.id),
      ref_code: props.affiliate.ref_code,
      utm_medium: props.affiliate.utm_medium,
      utm_campaign: props.affiliate.utm_campaign
    }
  } else {
    form.value = defaultForm()
  }
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// House convention: new lambdas return JSON errors; older ones return strings.
function parseError(err: any, fallback: string) {
  const data = err?.response?.data
  if (data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data
      if (parsed && parsed.error) return parsed
    } catch (e) { /* not JSON */ }
    if (typeof data === 'string' && data.length > 0 && data.length < 200) return { error: data }
  }
  return { error: fallback }
}

// Same shape the server generates: slugified name (lowercase, a-z0-9-, max 24)
// plus 4 random base36 chars. Uses the name field when filled.
function generateRefCode() {
  const slug = String(form.value.name || '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .replace(/-+$/, '') || 'affiliate'
  const suffix = Math.random().toString(36).slice(2, 6).padEnd(4, '0')
  form.value.ref_code = `${slug}-${suffix}`
}

function validate() {
  if (!form.value.name || !form.value.name.trim()) { errs.value = { name: 'Name is required' }; return false }
  if (!form.value.email || !form.value.email.trim()) { errs.value = { email: 'Email is required' }; return false }
  if (!form.value.affiliate_tier_id) { errs.value = { affiliate_tier_id: 'Select a tier' }; return false }
  if (!isEdit.value) {
    if (!normalizeUtmValue(form.value.utm_medium)) { errs.value = { utm_medium: 'Medium is required' }; return false }
    if (!isUtmValueValid(form.value.utm_medium)) { errs.value = { utm_medium: 'Medium may only use lowercase letters, numbers, dashes, and underscores, up to 64 characters' }; return false }
    if (!normalizeUtmValue(form.value.utm_campaign)) { errs.value = { utm_campaign: 'Campaign is required' }; return false }
    if (!isUtmValueValid(form.value.utm_campaign)) { errs.value = { utm_campaign: 'Campaign may only use lowercase letters, numbers, dashes, and underscores, up to 64 characters' }; return false }
  }
  return true
}

async function save() {
  errs.value = {}
  if (!validate()) {
    scrollToFirstError('.base-drawer__body')
    return
  }

  saving.value = true
  try {
    const config = await authConfig()
    const body: any = {
      name: form.value.name.trim(),
      email: form.value.email.trim(),
      type: form.value.type,
      affiliate_tier_id: form.value.affiliate_tier_id,
      promo_code_ids: form.value.promo_code_ids
    }
    if (isEdit.value) {
      await axios.put(`${baseUrl}/affiliates/${props.affiliate.id}`, body, config)
    } else {
      if (form.value.ref_code && form.value.ref_code.trim()) body.ref_code = form.value.ref_code.trim()
      body.utm_medium = normalizeUtmValue(form.value.utm_medium)
      body.utm_campaign = normalizeUtmValue(form.value.utm_campaign)
      await axios.post(`${baseUrl}/affiliates`, body, config)
    }
    proxy.$notify({
      type: 'success',
      message: isEdit.value ? 'Affiliate saved' : 'Affiliate created',
      icon: 'tim-icons icon-check-2'
    })
    emit('saved')
    emit('close')
  } catch (err: any) {
    const parsed = parseError(err, 'The affiliate could not be saved')
    if (parsed.field) {
      errs.value = { [parsed.field]: parsed.error }
      scrollToFirstError('.base-drawer__body')
    }
    proxy.$notify({ type: 'danger', message: parsed.error, icon: 'tim-icons icon-alert-circle-exc' })
  } finally {
    saving.value = false
  }
}

function onClose() {
  emit('close')
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape') onClose()
}

watch(() => props.show, (visible) => {
  if (visible) {
    document.addEventListener('keydown', onEscKey)
    loadForm()
  } else {
    document.removeEventListener('keydown', onEscKey)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<script lang="ts">
export default {
  name: 'affiliate-form-drawer'
}
</script>

<style scoped lang="scss">
.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

// Match base-button primary: the SEGMENT itself gets the exact btn-primary
// diagonal gradient (btn-styles($primary, $primary-states) via
// diagonal-gradient in _vendor-prefixes.scss); the inner button stays
// transparent like the promo Apply append, with tight horizontal padding.
::v-deep .el-input-group__append {
  background: #e14eca !important;
  background-image: linear-gradient(to bottom left, #e14eca, #ba54f5, #e14eca) !important;
  background-size: 210% 210% !important;
  background-position: top right !important;
  border: none !important;
  padding: 0 !important;
}
.generate-ref-append {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  height: 100%;
  padding: 0 12px;
  white-space: nowrap;
  transition: opacity 0.15s ease;

  &:hover { opacity: 0.9; }
}
</style>
